import React from 'react';
import { Box, Divider, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import { formatCurrency } from '../../utils/currencyUtils';
import { BRAND } from '../../theme/brandTokens';

/**
 * Stripe's standard published rates per payment method. These are indicative only — the
 * real charge varies with the card's origin and currency conversion, which is why the
 * dialog links out to Stripe's pricing page instead of trying to reproduce the matrix.
 *
 * Rates are per market. These cover the markets currently served (Portugal, Brazil);
 * revisit them when adding another, because some methods (Klarna in particular) are
 * priced differently per country even within the euro area.
 */
export const PAYMENT_METHOD_FEES = {
  Card: { percentage: 1.5, fixed: 0.25, currency: 'EUR' },
  MbWay: { percentage: 1.5, fixed: 0.25, currency: 'EUR' },
  Klarna: { percentage: 4.99, fixed: 0.4, currency: 'EUR', installments: true },
  Scalapay: { percentage: 4.99, fixed: 0.35, currency: 'EUR', installments: true },
  Sequra: { percentage: 4.99, fixed: 0.35, currency: 'EUR', installments: true },
  Pix: { percentage: 2, fixed: 0, currency: 'BRL' },
};

// Installment methods exist for expensive services, so a €20 example would not be telling.
const STANDARD_EXAMPLE_AMOUNT = 20;
const INSTALLMENTS_EXAMPLE_AMOUNT = 500;

const STRIPE_PRICING_URL = 'https://stripe.com/pricing';

const roundToCents = (value) => Math.round(value * 100) / 100;

const StorePaymentMethodFeeDialog = ({ open, onClose, methodType, methodLabel }) => {
  const { t } = useTranslation();
  const fee = methodType ? PAYMENT_METHOD_FEES[methodType] : null;

  if (!fee) {
    return null;
  }

  const amount = fee.installments ? INSTALLMENTS_EXAMPLE_AMOUNT : STANDARD_EXAMPLE_AMOUNT;
  const feeAmount = roundToCents((amount * fee.percentage) / 100 + fee.fixed);
  const netAmount = roundToCents(amount - feeAmount);

  const rateLabel = fee.fixed > 0
    ? `${fee.percentage}% + ${formatCurrency(fee.fixed, fee.currency)}`
    : `${fee.percentage}%`;

  const rows = [
    {
      label: t('paymentSettingsPage.customerPayments.fees.exampleService'),
      value: formatCurrency(amount, fee.currency),
    },
    {
      label: t('paymentSettingsPage.customerPayments.fees.processingFee', { rate: rateLabel }),
      value: `- ${formatCurrency(feeAmount, fee.currency)}`,
    },
  ];

  return (
    <ScrollableDialog
      open={open}
      onClose={onClose}
      title={t('paymentSettingsPage.customerPayments.fees.title', { method: methodLabel })}
      showCloseButton
      maxWidth="xs"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {rows.map((row) => (
          <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">{row.label}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{row.value}</Typography>
          </Box>
        ))}

        <Divider sx={{ my: 0.5 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('paymentSettingsPage.customerPayments.fees.youReceive')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.accentText, whiteSpace: 'nowrap' }}>
            {formatCurrency(netAmount, fee.currency)}
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
          {t('paymentSettingsPage.customerPayments.fees.disclaimer')}{' '}
          <Link href={STRIPE_PRICING_URL} target="_blank" rel="noopener noreferrer" sx={{ color: BRAND.accentText }}>
            {t('paymentSettingsPage.customerPayments.fees.stripePricingLink')}
          </Link>
        </Typography>
      </Box>
    </ScrollableDialog>
  );
};

export default StorePaymentMethodFeeDialog;
